/**
 * /community/members — Find Members.
 *
 * Public directory of contributors sorted by lifetime Social Capital.
 * Fed by GET /api/social-capital/members. Every card links to the
 * member's public passport (/passport/:handle) so viewers can dig in.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MapPin, ShieldCheck, Sparkles, Search, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";

const CATEGORIES = [
  { key: "",                 label: "All categories" },
  { key: "help_people",      label: "Help People" },
  { key: "save_food",        label: "Save Food" },
  { key: "protect_nature",   label: "Protect Nature" },
  { key: "support_farmers",  label: "Support Farmers" },
  { key: "share_knowledge",  label: "Share Knowledge" },
  { key: "reuse_give",       label: "Reuse & Give" },
  { key: "volunteer_serve",  label: "Volunteer & Serve" },
  { key: "build_community",  label: "Build Community" },
];

const TIER_COLORS = {
  bronze: "#B45309",
  silver: "#64748B",
  gold:   "#B45309",
};

export default function CommunityMembersPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams({ limit: "48" });
    if (category) params.set("category", category);
    http.get(`/social-capital/members?${params.toString()}`)
      .then((r) => { if (alive) setMembers(r.data?.members || []); })
      .catch(() => { if (alive) setMembers([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [category]);

  const visible = useMemo(() => {
    if (!query.trim()) return members;
    const q = query.trim().toLowerCase();
    return members.filter((m) =>
      (m.name || "").toLowerCase().includes(q) ||
      (m.handle || "").toLowerCase().includes(q) ||
      (m.city || "").toLowerCase().includes(q) ||
      (m.country || "").toLowerCase().includes(q) ||
      (m.profession || "").toLowerCase().includes(q),
    );
  }, [members, query]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 md:py-14" data-testid="sc-members-page">
      <SeoHead section="social-capital"
               title="Find Members · SocialCapital.cc"
               description="Discover contributors across the SocialCapital.cc network — filtered by category and searchable by name, city or handle."
               url="/community/members" />

      <header className="mb-8">
        <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: "#059669" }}>
          Community · Find members
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold" style={{ color: "#0f172a" }}>
          The people building Social Capital.
        </h1>
        <p className="mt-4 text-base sm:text-lg opacity-80 max-w-2xl">
          Browse contributors sorted by lifetime Social Capital. Filter by
          category or search by name, handle or city.
        </p>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" aria-hidden />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, handle, city…"
            className="w-full rounded-full border pl-9 pr-4 py-2 bg-white text-sm"
            style={{ borderColor: "rgba(0,0,0,0.15)" }}
            data-testid="sc-members-search"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="rounded-full border px-4 py-2 bg-white text-sm"
          style={{ borderColor: "rgba(0,0,0,0.15)" }}
          data-testid="sc-members-category"
        >
          {CATEGORIES.map((c) => (
            <option key={c.key || "all"} value={c.key}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Results */}
      {loading ? (
        <div className="py-20 text-center opacity-60" data-testid="sc-members-loading">
          <Loader2 className="w-6 h-6 animate-spin inline-block" />
          <div className="text-sm mt-2">Loading members…</div>
        </div>
      ) : visible.length === 0 ? (
        <div className="py-20 text-center opacity-60" data-testid="sc-members-empty">
          <Sparkles className="w-6 h-6 inline-block mb-2" />
          <div>No members match those filters yet. Try widening your search.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sc-members-grid">
          {visible.map((m) => (
            <MemberCard key={m.id} m={m} />
          ))}
        </div>
      )}

      {/* Footer CTA */}
      <aside
        className="mt-12 rounded-2xl border p-6 flex flex-wrap items-center justify-between gap-4"
        style={{ borderColor: "rgba(5,150,105,0.25)", background: "#ECFDF5" }}
      >
        <div>
          <div className="text-xs font-mono uppercase tracking-widest" style={{ color: "#059669" }}>
            Not on the wall yet?
          </div>
          <div className="font-display text-lg sm:text-xl mt-1">
            Log your first contribution — you'll appear here as soon as it's verified.
          </div>
        </div>
        <Link to="/earn/submit"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white"
              style={{ background: "#059669" }}
              data-testid="sc-members-cta">
          Submit an action <ArrowRight className="w-4 h-4" />
        </Link>
      </aside>
    </div>
  );
}

function MemberCard({ m }) {
  const initial = (m.name || m.handle || "?").trim().charAt(0).toUpperCase();
  const tier = (m.verification_tier || "").toLowerCase();
  const tierColor = TIER_COLORS[tier];
  return (
    <Link
      to={`/passport/${encodeURIComponent(m.handle || m.id)}`}
      className="rounded-2xl border p-5 bg-white hover:border-emerald-300 transition-colors flex gap-4 items-start"
      style={{ borderColor: "rgba(0,0,0,0.08)" }}
      data-testid={`sc-member-${m.id}`}
    >
      {m.avatar_url ? (
        <img src={m.avatar_url} alt=""
             className="w-14 h-14 rounded-full object-cover shrink-0"
             loading="lazy" />
      ) : (
        <span
          className="w-14 h-14 rounded-full inline-flex items-center justify-center text-white text-lg font-bold shrink-0"
          style={{ background: "linear-gradient(135deg,#059669,#10b981)" }}
        >{initial}</span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <div className="font-semibold text-base truncate">{m.name || m.handle}</div>
          {tierColor && (
            <ShieldCheck className="w-4 h-4 shrink-0" style={{ color: tierColor }}
                         title={`${tier} verified`} aria-hidden />
          )}
        </div>
        {m.handle && (
          <div className="text-xs opacity-60 truncate">@{m.handle}</div>
        )}
        {m.profession && (
          <div className="text-xs opacity-75 mt-1 truncate">{m.profession}</div>
        )}
        {(m.city || m.country) && (
          <div className="text-xs opacity-60 mt-1 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" aria-hidden />
            <span>{[m.city, m.country].filter(Boolean).join(", ")}</span>
          </div>
        )}
        <div className="mt-3 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold"
             style={{ background: "#ECFDF5", color: "#065F46" }}>
          <Sparkles className="w-3 h-3" />
          <span className="tabular-nums">{m.social_capital.toLocaleString()}</span>
          <span>SC</span>
        </div>
      </div>
    </Link>
  );
}
